import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useAuth } from '../context/AuthContext';

/**
 * Login Page - User authentication page
 *
 * Features:
 * - Login form with email and password fields
 * - Form validation (required fields)
 * - Error message display for invalid credentials
 * - Loading state during authentication
 * - Redirect to dashboard on successful login
 *
 * Requirements: 13.2, 13.3
 */
export function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const { login, loginWithFederate, isLoading: authLoading, isFederationEnabled } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFederating, setIsFederating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation state
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  /**
   * Validate form inputs
   * @returns true if form is valid, false otherwise
   */
  const validateForm = useCallback((): boolean => {
    let isValid = true;

    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    } else {
      setEmailError(null);
    }

    // Validate password
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else {
      setPasswordError(null);
    }

    return isValid;
  }, [email, password]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (event: React.FormEvent): Promise<void> => {
      event.preventDefault();

      // Clear previous error
      setError(null);

      // Validate form
      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      try {
        await login(email.trim(), password);
        // Redirect to dashboard on success
        navigate('/dashboard', { replace: true });
      } catch (err) {
        // Display error message
        const errorMessage =
          err instanceof Error ? err.message : 'An error occurred during login. Please try again.';
        setError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, password, login, navigate, validateForm]
  );

  /**
   * Handle email input change
   */
  const handleEmailChange = useCallback(
    (event: { detail: { value: string } }): void => {
      setEmail(event.detail.value);
      // Clear email error when user starts typing
      if (emailError) {
        setEmailError(null);
      }
      // Clear general error when user modifies input
      if (error) {
        setError(null);
      }
    },
    [emailError, error]
  );

  /**
   * Handle password input change
   */
  const handlePasswordChange = useCallback(
    (event: { detail: { value: string } }): void => {
      setPassword(event.detail.value);
      // Clear password error when user starts typing
      if (passwordError) {
        setPasswordError(null);
      }
      // Clear general error when user modifies input
      if (error) {
        setError(null);
      }
    },
    [passwordError, error]
  );

  const isLoading = isSubmitting || authLoading;

  const handleFederateSignIn = useCallback(async (): Promise<void> => {
    setError(null);
    setIsFederating(true);
    try {
      await loginWithFederate();
      // If the silent-recovery path resolved an existing session, the user
      // is already signed in and we navigate. The redirect path navigates
      // away from the page entirely, so this navigate() runs only on the
      // recovery branch.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setIsFederating(false);
      const message = err instanceof Error ? err.message : 'Failed to start Midway sign in.';
      setError(message);
    }
  }, [loginWithFederate, navigate]);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description="Sign in to access your training sessions">
          Login
        </Header>
      }
    >
      <Container>
        <form onSubmit={handleSubmit}>
          <Form
            actions={
              !isFederationEnabled ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="primary" formAction="submit" loading={isLoading} disabled={isLoading}>
                    {isLoading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </SpaceBetween>
              ) : undefined
            }
          >
            <SpaceBetween direction="vertical" size="l">
              {/* Error alert for authentication failures */}
              {error && (
                <Alert type="error" dismissible onDismiss={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Federation sign-in (Midway via Federate) — preferred path */}
              {isFederationEnabled && (
                <Box>
                  <SpaceBetween direction="vertical" size="xs">
                    <Button
                      variant="primary"
                      onClick={handleFederateSignIn}
                      loading={isFederating}
                      disabled={isLoading || isFederating}
                      fullWidth
                      formAction="none"
                    >
                      {isFederating ? 'Redirecting to Midway...' : 'Sign in with Midway'}
                    </Button>
                    <Box variant="small" color="text-status-inactive">
                      Use your Amazon credentials. Recommended for all team members.
                    </Box>
                  </SpaceBetween>
                </Box>
              )}

              {/* Email field */}
              {!isFederationEnabled && (
                <FormField
                  label="Email"
                  description="Enter your email address"
                  errorText={emailError}
                  constraintText="Required"
                >
                  <Input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="user@example.com"
                    disabled={isLoading}
                    ariaRequired={true}
                    autoComplete={true}
                    inputMode="email"
                  />
                </FormField>
              )}

              {/* Password field */}
              {!isFederationEnabled && (
                <FormField
                  label="Password"
                  description="Enter your password"
                  errorText={passwordError}
                  constraintText="Required"
                >
                  <Input
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="Enter your password"
                    disabled={isLoading}
                    ariaRequired={true}
                  />
                </FormField>
              )}
            </SpaceBetween>
          </Form>
        </form>
      </Container>
    </ContentLayout>
  );
}

export default LoginPage;
